'use client';

import { text, type Localized } from '../shared/primitives';
import { JsonLdScript } from './homeShared';

/**
 * Site-wide structured data for the home page: an `Organization` and a
 * `WebSite` with a `SearchAction` (the sitelinks search box). Renders nothing
 * visible.
 *
 * <p>It carries no `AggregateRating` and no contact details on purpose: those
 * are facts that must come from a real, current source, and a stale or invented
 * rating in structured data is a search-engine policy violation, not just a
 * wrong number.
 *
 * <p>Invalid data is worse than none, so the block emits nothing when `name` is
 * empty or `url` is not an absolute `https://` URL. A relative `logo` is
 * resolved against `url`; `sameAs` keeps only absolute https profile URLs.
 */
export interface SpatialKitchenSiteSchemaProps {
  name?: Localized;
  /** The site's canonical home URL, e.g. `https://example.co.uk/`. */
  url?: string;
  logo?: string;
  /** Profile URLs (social accounts, marketplaces) that are the same organisation. */
  sameAs?: string[];
  /** The storefront's search page; the query goes in `q`. Defaults to `/search`. */
  searchPath?: string;
}

function httpsUrl(value: unknown, base?: URL): URL | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = base ? new URL(value.trim(), base) : new URL(value.trim());
    return url.protocol === 'https:' && url.hostname ? url : null;
  } catch {
    // Not a URL at all: the caller treats it exactly like a missing one.
    return null;
  }
}

/** A same-origin path such as `/search`; anything else (a full URL, `//host`) falls back. */
function searchPathOr(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const path = value.trim();
  return /^\/(?!\/)[^?#\s]*$/.test(path) ? path : fallback;
}

export function siteSchemaData(props: SpatialKitchenSiteSchemaProps): Record<string, unknown> | null {
  const name = text(props.name);
  const home = httpsUrl(props.url);
  if (!name || !home) return null;
  const homeHref = home.href;
  const logo = httpsUrl(props.logo, home);
  const sameAs = (Array.isArray(props.sameAs) ? props.sameAs : [])
    .map((entry) => httpsUrl(entry))
    .filter((url): url is URL => url !== null)
    .map((url) => url.href);
  const searchPath = searchPathOr(props.searchPath, '/search');

  const organization: Record<string, unknown> = {
    '@type': 'Organization',
    '@id': `${home.origin}/#organization`,
    name,
    url: homeHref,
  };
  if (logo) organization.logo = logo.href;
  if (sameAs.length > 0) organization.sameAs = sameAs;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      {
        '@type': 'WebSite',
        '@id': `${home.origin}/#website`,
        name,
        url: homeHref,
        publisher: { '@id': `${home.origin}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            // The braces must reach the page literally: they are the template, not a value.
            urlTemplate: `${home.origin}${searchPath}?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };
}

export function SpatialKitchenSiteSchema(props: SpatialKitchenSiteSchemaProps) {
  const data = siteSchemaData(props);
  return data ? <JsonLdScript data={data} /> : null;
}
